/**
 * NoteGenerationProgress
 *
 * A progress indicator component showing the current state of note generation workflow.
 * Displays workflow steps as a stepper with the current step highlighted.
 */

import React, { useMemo } from 'react';
import {
  AutoAwesome as SparkleIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  CircularProgress,
  Collapse,
  Fade,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';

import type { EncounterAnalysis,WorkflowNode } from '../hooks/useNoteGenerationStreaming';

/**
 * Fallback workflow steps (used when manifest not yet received).
 * These match the step names emitted by the backend.
 */
const FALLBACK_WORKFLOW_STEPS: Array<{ id: string; displayName: string; visible: boolean; order: number }> = [
  { id: 'initialize', displayName: 'Starting', visible: true, order: 0 },
  { id: 'create_pending_note', displayName: 'Creating note', visible: true, order: 1 },
  { id: 'collect_context', displayName: 'Gathering context', visible: true, order: 2 },
  { id: 'resolve_template', displayName: 'Loading template', visible: true, order: 3 },
  { id: 'analyze_encounter', displayName: 'Analyzing encounter', visible: true, order: 4 },
  { id: 'generate_note', displayName: 'Generating note', visible: true, order: 5 },
  { id: 'populate_sections', displayName: 'Building sections', visible: true, order: 6 },
  { id: 'finalize_note', displayName: 'Finalizing', visible: true, order: 7 },
];

interface NoteGenerationProgressProps {
  /** Current workflow step name */
  currentStep: string | null;
  /** List of completed step names */
  completedSteps: string[];
  /** Workflow nodes from manifest (for dynamic progress display). Empty array means manifest hasn't loaded yet. */
  workflowNodes?: WorkflowNode[];
  /** Whether the workflow is complete */
  isComplete?: boolean;
  /** Whether an error occurred */
  hasError?: boolean;
  /** Variant: 'compact' for inline, 'full' for detailed view */
  variant?: 'compact' | 'full';
  /** Encounter analysis data from ACTIVITY_SNAPSHOT event */
  encounterAnalysis?: EncounterAnalysis | null;
}

/**
 * Get step status for display
 */
function getStepStatus(
  stepKey: string,
  currentStep: string | null,
  completedSteps: string[],
): 'completed' | 'current' | 'pending' {
  if (completedSteps.includes(stepKey)) {
    return 'completed';
  }
  if (currentStep === stepKey) {
    return 'current';
  }
  return 'pending';
}

/**
 * Get a user-friendly label for the current step
 */
function getStepLabel(
  stepKey: string | null,
  workflowNodes: WorkflowNode[],
): string {
  if (!stepKey) return 'Preparing...';
  const node = workflowNodes.find(n => n.id === stepKey);
  return node?.displayName || stepKey;
}

/**
 * Calculate progress percentage
 */
function getProgressPercentage(
  currentStep: string | null,
  completedSteps: string[],
  workflowNodes: WorkflowNode[],
): number {
  if (!currentStep && completedSteps.length === 0) return 0;

  const currentIndex = workflowNodes.findIndex(n => n.id === currentStep);
  const completedCount = completedSteps.length;

  // Use max of current step index or completed count
  const effectiveProgress = Math.max(currentIndex >= 0 ? currentIndex : 0, completedCount);

  // Return percentage (current step counts as partial progress)
  return Math.min(((effectiveProgress + 0.5) / workflowNodes.length) * 100, 100);
}

/**
 * Compact variant - single line with progress bar
 */
const CompactProgress: React.FC<NoteGenerationProgressProps> = ({
  currentStep,
  completedSteps,
  workflowNodes,
  isComplete,
  hasError,
}) => {
  const theme = useTheme();
  const manifestLoaded = workflowNodes !== undefined && workflowNodes.length > 0;
  const nodesToUse = manifestLoaded ? workflowNodes : FALLBACK_WORKFLOW_STEPS;
  const progress = getProgressPercentage(currentStep, completedSteps, nodesToUse);
  const label = isComplete ? 'Complete!' : getStepLabel(currentStep, nodesToUse);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderRadius: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      }}
    >
      {isComplete ? (
        <CheckIcon sx={{ color: 'success.main', fontSize: 24 }} />
      ) : hasError ? (
        <SparkleIcon sx={{ color: 'error.main', fontSize: 24 }} />
      ) : (
        <CircularProgress size={24} thickness={4} />
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
          {hasError
            ? 'Status: Error generating note'
            : manifestLoaded
              ? `Status: ${label}`
              : 'Status: Connecting...'}
        </Typography>
        {!isComplete && !hasError && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mt: 0.5,
              height: 4,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              '& .MuiLinearProgress-bar': {
                borderRadius: 2,
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

/**
 * Full variant - shows only current step with cycling animation
 */
const FullProgress: React.FC<NoteGenerationProgressProps> = ({
  currentStep,
  completedSteps,
  workflowNodes,
  isComplete,
  hasError,
  encounterAnalysis,
}) => {
  const theme = useTheme();

  // Check if manifest has loaded (empty array means manifest hasn't loaded yet)
  const manifestLoaded = workflowNodes !== undefined && workflowNodes.length > 0;
  const nodesToUse = manifestLoaded ? workflowNodes : FALLBACK_WORKFLOW_STEPS;

  // Filter to only visible nodes and ensure they're sorted by order
  const visibleSteps = useMemo(() => {
    return nodesToUse.filter(n => n.visible).sort((a, b) => a.order - b.order);
  }, [nodesToUse]);

  // Find current step node
  const currentStepNode = useMemo(() => {
    if (!currentStep) return null;
    return visibleSteps.find(n => n.id === currentStep);
  }, [currentStep, visibleSteps]);

  // Track step key for animation - changes when step changes
  const stepKey = useMemo(() => {
    return currentStepNode?.id || 'none';
  }, [currentStepNode]);

  // Calculate progress percentage
  const progressPercentage = useMemo(() => {
    if (!currentStepNode || visibleSteps.length === 0) return 0;
    const currentIndex = visibleSteps.findIndex(n => n.id === currentStepNode.id);
    const completedCount = completedSteps.length;
    const totalSteps = visibleSteps.length;
    
    // Progress based on completed steps + current step (partial)
    return Math.min(((completedCount + 0.5) / totalSteps) * 100, 100);
  }, [currentStepNode, completedSteps, visibleSteps]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {isComplete ? (
          <SparkleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        ) : (
          <CircularProgress size={20} thickness={4} />
        )}
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {isComplete
            ? 'Status: Note Generated'
            : hasError
              ? 'Status: Generation Failed'
              : currentStepNode
                ? `Status: ${currentStepNode.displayName}`
                : manifestLoaded
                  ? 'Status: Preparing...'
                  : 'Status: Transcribing audio'}
        </Typography>
      </Box>

      {/* Progress bar with animation */}
      {manifestLoaded && !isComplete && currentStepNode && (
        <Fade key={stepKey} in={!!currentStepNode} timeout={400}>
          <Box sx={{ width: '100%' }}>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                },
              }}
            />
          </Box>
        </Fade>
      )}

      {/* Encounter Analysis Section */}
      <Collapse in={!!encounterAnalysis?.summary} timeout={400}>
        {encounterAnalysis?.summary && (
          <Box
            sx={{
              mt: 1,
              pt: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Encounter Summary
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{
                lineHeight: 1.5,
              }}
            >
              {encounterAnalysis.summary}
            </Typography>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

/**
 * NoteGenerationProgress component
 *
 * Shows workflow progress during note generation streaming.
 */
export const NoteGenerationProgress: React.FC<NoteGenerationProgressProps> = props => {
  const { variant = 'compact' } = props;

  if (variant === 'full') {
    return <FullProgress {...props} />;
  }

  return <CompactProgress {...props} />;
};

export default NoteGenerationProgress;
